/* dermaQ landing page — one IIFE, no globals of its own, nothing on window.
 *
 * The single exception is window.dataLayer, which is GTM's own contract:
 * an array you are required to create if it does not exist. It is only
 * touched when "Push events to window.dataLayer" is enabled in the section.
 *
 * Everything else is local. Every element it binds to lives inside
 * [data-dqlp-root], so nothing here can reach another template.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * small helpers
   * ------------------------------------------------------------------ */

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function closest(node, selector) {
    if (!node || !node.closest) return null;
    try { return node.closest(selector); } catch (e) { return null; }
  }

  /* Live instances, one per [data-dqlp-root]. The theme editor replaces the
     section's markup on every keystroke, so each instance has to be able to
     take its timers and its window listeners with it — otherwise an hour of
     editing leaves a hundred intervals running against detached DOM and the
     preview turns to treacle. */
  var instances = [];

  function dropInstance(index) {
    var entry = instances[index];
    instances.splice(index, 1);
    try { entry.destroy(); } catch (e) { /* teardown is best effort */ }
  }

  function dropDetached() {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (!document.documentElement.contains(instances[i].root)) dropInstance(i);
    }
  }

  function instanceFor(node) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root.contains(node)) return instances[i];
    }
    return null;
  }

  function reduceMotion() {
    try {
      return matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error('dqlp request failed');
          err.status = res.status;
          err.body = data;
          throw err;
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * money — formatted from cart.js cents, never from local arithmetic
   * ------------------------------------------------------------------ */

  function withDelimiters(cents, precision, thousands, decimal) {
    var fixed = (Number(cents || 0) / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /* Mirrors Liquid's money_without_trailing_zeros: whole amounts lose the
     decimals, everything else keeps them. The layout was drawn against "$89",
     not "$89.00", and the difference is a wrapped line on a 390px screen. */
  function applyMoneyFormat(cents, format) {
    var whole = Number(cents || 0) % 100 === 0;
    var d = whole ? 0 : 2;
    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount': return withDelimiters(cents, d, ',', '.');
        case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(cents, d, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_space_separator': return withDelimiters(cents, d, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(cents, d, "'", '.');
        default: return withDelimiters(cents, d, ',', '.');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * per-root instance
   * ------------------------------------------------------------------ */

  function init(root) {
    if (root.getAttribute('data-dqlp-ready') === '1') return;
    root.setAttribute('data-dqlp-ready', '1');

    var configNode = root.querySelector('[data-dqlp-config]');
    if (!configNode) return;

    var cfg;
    try {
      cfg = JSON.parse(configNode.textContent);
    } catch (e) {
      return;
    }
    cfg.tiers = cfg.tiers || [];
    cfg.gifts = cfg.gifts || [];
    cfg.strings = cfg.strings || {};
    cfg.routes = cfg.routes || {};
    cfg.analytics = cfg.analytics || {};
    cfg.managedVariantIds = cfg.managedVariantIds || [];

    var state = {
      tierId: cfg.defaultTierId || null,
      cart: null,
      busy: false,
      slide: 0,
      seenSlides: {},
      utm: {},
      utmApplied: false,
      drawerOpen: false,
      lastFocus: null,
      countdownTimer: 0
    };

    /* Everything this instance attaches outside its own root, so `destroy`
       below can hand it all back. */
    var observers = [];

    function $(sel) { return root.querySelector(sel); }
    function $$(sel) { return toArray(root.querySelectorAll(sel)); }

    function tierById(id) {
      for (var i = 0; i < cfg.tiers.length; i++) {
        if (cfg.tiers[i].id === id) return cfg.tiers[i];
      }
      return null;
    }

    function firstAvailableTier() {
      for (var i = 0; i < cfg.tiers.length; i++) {
        if (cfg.tiers[i].available) return cfg.tiers[i];
      }
      return cfg.tiers[0] || null;
    }

    function money(cents) {
      var currency =
        (state.cart && state.cart.currency) || cfg.cartCurrency || cfg.shopCurrency;
      if (currency && currency === cfg.shopCurrency && cfg.moneyFormat) {
        return applyMoneyFormat(cents, cfg.moneyFormat);
      }
      try {
        var digits = Number(cents || 0) % 100 === 0 ? 0 : 2;
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        }).format(Number(cents || 0) / 100);
      } catch (e) {
        return applyMoneyFormat(cents, cfg.moneyFormat || '${{amount}}');
      }
    }

    /* ---------------- stylesheet integrity ---------------- */

    /* The last rule in the CSS sets --dqlp-css-ok. If we cannot read it, the
       file never arrived or arrived truncated — which looks like a broken
       layout rather than a missing file, so say so out loud. */
    function verifyStylesheet() {
      var ok = getComputedStyle(root).getPropertyValue('--dqlp-css-ok').trim();
      if (ok === '1') return true;
      var message =
        'dermaq-landing.css is missing or was truncated when it was uploaded. ' +
        'The complete file ends with the rule that sets --dqlp-css-ok. ' +
        'Re-upload assets/dermaq-landing.css.';
      try { console.error('[dermaQ landing] ' + message); } catch (e) {}
      try {
        if (self.Shopify && self.Shopify.designMode) {
          var box = document.createElement('div');
          box.setAttribute('style',
            'margin:12px;padding:14px 16px;border:2px solid #D72B2B;border-radius:10px;' +
            'background:#FFF0F0;color:#7A1414;font:600 13px/1.5 system-ui,-apple-system,sans-serif');
          box.textContent = message;
          root.insertBefore(box, root.firstChild);
        }
      } catch (e) {}
      return false;
    }

    /* ---------------- sticky offset under the theme header ---------------- */

    /* When the bar is pinned above the theme header, publish its real height so
       the emitted stylesheet can push the header group down by exactly that. */
    /* Move the bar to the very top of the document.

       The bar has to sit above the theme's header, but this section renders
       below it, so the bar was fixed and the page was padded to make room.
       That arithmetic is what produced the band of white: any spacing the theme
       already carried above its own header was added to ours, and no amount of
       measuring reliably tells the two apart.

       Relocating removes the arithmetic instead of correcting it. The bar goes
       into a host of its own, first thing in the body, in normal flow — so it
       reserves exactly its own height and there is nothing left to compensate
       for. The host is sticky rather than the bar, because a sticky child only
       holds while its parent is on screen and the host is no taller than the
       bar itself; sticking the host makes it hold for the whole page.

       The host carries .dqlp-root so every scoped rule still applies, and
       destroy() puts the bar back where it came from. */
    var barHome = null;

    function hoistBar() {
      if (barHome) return;
      if (root.getAttribute('data-announce-position') !== 'above_header') return;
      if (!document.body) return;
      var bar = $('[data-dqlp-announce]');
      if (!bar) return;

      /* Preferred: the slot the merchant placed inside the theme's own header
         group, using the dermaq-announce section. There the bar is simply part
         of the header — the theme's spacing, stacking and sticky behaviour all
         apply to it, and nothing here has to compensate for any of them. */
      var slot = document.querySelector('[data-dqlp-bar-slot]');
      var host = slot;
      var owned = false;

      if (!host) {
        /* Nothing placed: fall back to a host of our own at the very top of
           the document. Still in the flow, so still no padding to compute —
           only now the theme's sticky header needs an offset, because the two
           are no longer travelling together. */
        host = document.createElement('div');
        host.className = 'dqlp-root dqlp-barhost';
        host.setAttribute('data-dqlp-bar-host', '');
        var inline = root.getAttribute('style');
        if (inline) host.setAttribute('style', inline);
        document.body.insertBefore(host, document.body.firstChild);
        owned = true;
      }

      barHome = { parent: bar.parentNode, next: bar.nextSibling, host: host, owned: owned };
      host.appendChild(bar);

      /* The placeholder padding has done its job either way. */
      document.documentElement.style.setProperty('--dqlp-bar-pad', '0px');
      if (owned) {
        publishBarHeight();
      } else {
        /* Inside the header group the bar and the header stick as one, so the
           theme's sticky offset must stay at nothing. */
        document.documentElement.style.setProperty('--dqlp-bar-h', '0px');
      }
    }

    function dropBar() {
      if (!barHome) return;
      var bar = barHome.host.querySelector('[data-dqlp-announce]');
      if (bar && barHome.parent) barHome.parent.insertBefore(bar, barHome.next);
      if (barHome.owned && barHome.host.parentNode) {
        barHome.host.parentNode.removeChild(barHome.host);
      }
      barHome = null;
      document.documentElement.style.removeProperty('--dqlp-bar-pad');
    }

    /* Only the theme's own sticky header needs this now: it parks at top 0 by
       default and would slide under the bar on scroll.

       Not when the bar is in the merchant's slot. There the bar is part of the
       header group and the two stick as one, so an offset only pushes the
       header down by the bar's height a second time — which is precisely the
       band of white this was meant to remove. hoistBar sets the offset to zero
       for that case and this used to measure it straight back, one line later. */
    function publishBarHeight() {
      if (barHome && !barHome.owned) {
        document.documentElement.style.setProperty('--dqlp-bar-h', '0px');
        return;
      }
      if (root.getAttribute('data-announce-position') !== 'above_header') return;
      var bar = (barHome && barHome.host.querySelector('[data-dqlp-announce]')) ||
                $('[data-dqlp-announce]');
      if (!bar) return;
      var h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0 && h < 240) {
        document.documentElement.style.setProperty('--dqlp-bar-h', h + 'px');
      }
    }

    function applyStickyOffset() {
      if (root.getAttribute('data-announce-position') === 'above_header') return;
      if (root.getAttribute('data-announce-sticky') !== 'true') return;
      if (cfg.announceOffset) return;              /* merchant set it by hand */
      if (cfg.themeChrome === 'hide_all') return;  /* nothing left above us */
      try {
        var header = document.querySelector('sticky-header') ||
                     document.querySelector('.shopify-section-group-header-group[id$="__header"]');
        if (!header) return;
        var position = getComputedStyle(header).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        var height = Math.round(header.getBoundingClientRect().height);
        if (height > 0 && height < 200) {
          root.style.setProperty('--dqlp-announce-offset', height + 'px');
        }
      } catch (e) { /* the header is the theme's, never a hard dependency */ }
    }

    /* ---------------- measurement ---------------- */

    function track(name, payload) {
      var data = payload || {};
      if (cfg.analytics.dataLayer) {
        try {
          var host = self;
          host.dataLayer = host.dataLayer || [];
          var evt = { event: name };
          for (var key in data) {
            if (has(data, key)) evt[key] = data[key];
          }
          host.dataLayer.push(evt);
        } catch (e) { /* measurement must never break the page */ }
      }
      if (cfg.analytics.shopifyPixel) {
        try {
          var shopify = self.Shopify;
          if (shopify && shopify.analytics && typeof shopify.analytics.publish === 'function') {
            shopify.analytics.publish(name, data);
          }
        } catch (e) { /* same */ }
      }
    }

    function currencyCode() {
      return (state.cart && state.cart.currency) || cfg.cartCurrency || cfg.shopCurrency || '';
    }

    function cartItemsPayload(cart) {
      return ((cart && cart.items) || []).map(function (item) {
        return {
          variant_id: item.id,
          product_id: item.product_id,
          name: item.product_title,
          quantity: item.quantity,
          price: Number(item.final_price || 0) / 100
        };
      });
    }

    /* ---------------- token templates ---------------- */

    function tokensFor(tier) {
      return {
        price: tier ? tier.price : '',
        was: tier ? tier.was : '',
        save: tier ? tier.save : '',
        gifts: tier ? tier.gifts : '',
        gifts_count: tier && tier.giftsCount != null ? String(tier.giftsCount) : '',
        tier: tier ? (tier.shortName || tier.name) : '',
        total_value: tier ? tier.totalValue : '',
        device_price: cfg.devicePrice || '',
        oil_price: cfg.oilPrice || '',
        pct: cfg.marketPct || '',
        credit: cfg.credit || ''
      };
    }

    function fill(template, map) {
      return String(template == null ? '' : template).replace(/\[(\w+)\]/g, function (whole, key) {
        return has(map, key) ? map[key] : whole;
      });
    }

    /* Renders a token template into real nodes, so the merchant's copy is
       never passed through innerHTML. */
    function fillInto(node, template, map, amountClass) {
      var text = String(template == null ? '' : template);
      node.textContent = '';
      var pattern = /\[(\w+)\]/g;
      var cursor = 0;
      var match;
      while ((match = pattern.exec(text)) !== null) {
        if (match.index > cursor) {
          node.appendChild(document.createTextNode(text.slice(cursor, match.index)));
        }
        if (has(map, match[1])) {
          if (amountClass) {
            node.appendChild(el('span', amountClass, map[match[1]]));
          } else {
            node.appendChild(document.createTextNode(map[match[1]]));
          }
        } else {
          node.appendChild(document.createTextNode(match[0]));
        }
        cursor = match.index + match[0].length;
      }
      if (cursor < text.length) {
        node.appendChild(document.createTextNode(text.slice(cursor)));
      }
    }

    /* ---------------- one-line headline ---------------- */

    /* The headline must hold one line at every width, but a merchant can type
       anything into it. Rather than nowrap and hope, shrink until it fits:
       the copy is never clipped and never wraps, whatever the price is. */
    function fitHeadlines() {
      $$('[data-dqlp-fit]').forEach(function (node) {
        var max = parseFloat(node.getAttribute('data-dqlp-fit-max')) || 32;
        var min = parseFloat(node.getAttribute('data-dqlp-fit-min')) || 15;
        node.style.whiteSpace = 'nowrap';
        node.style.fontSize = max + 'px';
        var size = max;
        var guard = 0;
        while (node.scrollWidth > node.clientWidth + 0.5 && size > min && guard < 80) {
          size -= 0.5;
          node.style.fontSize = size + 'px';
          guard += 1;
        }
        /* If even the floor cannot hold it, let it wrap rather than clip. */
        node.style.whiteSpace = node.scrollWidth > node.clientWidth + 0.5 ? 'normal' : 'nowrap';
      });
    }

    /* ---------------- tier selection ---------------- */

    function applyTier(tier) {
      if (!tier) return;
      state.tierId = tier.id;
      var map = tokensFor(tier);

      $$('[data-dqlp-price]').forEach(function (node) { node.textContent = tier.price; });

      $$('[data-dqlp-was]').forEach(function (node) {
        node.textContent = tier.was;
        node.hidden = !tier.showWas;
      });

      $$('[data-dqlp-total-value]').forEach(function (node) { node.textContent = tier.totalValue; });
      $$('[data-dqlp-total-wrap]').forEach(function (node) { node.hidden = !tier.showTotalValue; });

      $$('[data-dqlp-text]').forEach(function (node) {
        var key = node.getAttribute('data-dqlp-text');
        if (key === 'comparePrice') {
          node.textContent = fill(node.getAttribute('data-dqlp-template') || node.textContent, map);
          return;
        }
        if (!has(cfg.strings, key)) return;
        node.textContent = fill(cfg.strings[key], map);
      });

      $$('[data-dqlp-html]').forEach(function (node) {
        var key = node.getAttribute('data-dqlp-html');
        if (!has(cfg.strings, key)) return;
        fillInto(node, cfg.strings[key], map, 'dqlp-announce__amount');
      });

      $$('[data-dqlp-oil-row]').forEach(function (node) { node.hidden = !tier.includesOil; });

      $$('[data-dqlp-stack-gift]').forEach(function (node) {
        var needsKit = node.getAttribute('data-requires-kit') === 'true';
        node.hidden = needsKit && !tier.unlocksAllGifts;
      });

      $$('[data-dqlp-gift]').forEach(function (node) {
        var needsKit = node.getAttribute('data-requires-kit') === 'true';
        node.setAttribute('data-locked', needsKit && !tier.unlocksAllGifts ? 'true' : 'false');
      });

      $$('[data-dqlp-tier]').forEach(function (node) {
        var selected = node.getAttribute('data-dqlp-tier') === tier.id;
        node.setAttribute('data-selected', selected ? 'true' : 'false');
        var input = node.querySelector('[data-dqlp-tier-input]');
        if (input) input.checked = selected;
      });

      fitHeadlines();
      if (state.drawerOpen || state.cart) renderDrawer();
    }

    function selectTier(id, fromUser) {
      var tier = tierById(id);
      if (!tier || !tier.available) return;
      applyTier(tier);
      clearError();
      if (fromUser) {
        track('tier_selected', {
          tier_name: tier.name,
          tier_id: tier.id,
          variant_id: tier.variantId,
          value: Number(tier.priceCents || 0) / 100,
          currency: currencyCode()
        });
      }
    }

    function markUnavailable(tier) {
      tier.available = false;
      var node = root.querySelector('[data-dqlp-tier="' + tier.id + '"]');
      if (node) {
        node.setAttribute('data-available', 'false');
        var input = node.querySelector('[data-dqlp-tier-input]');
        if (input) input.disabled = true;
      }
      var next = firstAvailableTier();
      if (next && next.available) applyTier(next);
    }

    /* ---------------- inline error, never an alert ---------------- */

    function showError(message) {
      $$('[data-dqlp-error]').forEach(function (node) {
        node.textContent = message || cfg.strings.genericError || '';
        node.setAttribute('data-visible', 'true');
      });
    }

    function clearError() {
      $$('[data-dqlp-error]').forEach(function (node) {
        node.textContent = '';
        node.setAttribute('data-visible', 'false');
      });
    }

    /* ---------------- gallery ---------------- */

    var gallery = $('[data-dqlp-gallery]');
    var thumbs = $$('[data-dqlp-thumb]');
    var slides = gallery ? toArray(gallery.querySelectorAll('[data-dqlp-slide]')) : [];

    function currentSlide() {
      if (!gallery || !slides.length) return 0;
      var width = slides[0].getBoundingClientRect().width;
      if (!width) return 0;
      var step = width + 12;
      var index = Math.round(gallery.scrollLeft / step);
      return Math.max(0, Math.min(slides.length - 1, index));
    }

    function setSlide(index) {
      if (index === state.slide) return;
      state.slide = index;
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (!state.seenSlides[index]) {
        state.seenSlides[index] = true;
        track('gallery_slide_view', { index: index, slide_number: index + 1 });
      }
    }

    function goToSlide(index) {
      if (!gallery || !slides[index]) return;
      var left = slides[index].offsetLeft - gallery.offsetLeft;
      try {
        gallery.scrollTo({ left: left, behavior: reduceMotion() ? 'auto' : 'smooth' });
      } catch (e) {
        gallery.scrollLeft = left;
      }
      setSlide(index);
    }

    if (gallery) {
      var scrollPending = 0;
      gallery.addEventListener('scroll', function () {
        if (scrollPending) return;
        scrollPending = requestAnimationFrame(function () {
          scrollPending = 0;
          setSlide(currentSlide());
        });
      }, { passive: true });

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          goToSlide(parseInt(thumb.getAttribute('data-dqlp-thumb'), 10) || 0);
        });
      });

      if (slides.length) {
        state.seenSlides[0] = true;
        track('gallery_slide_view', { index: 0, slide_number: 1 });
      }
    }

    /* ---------------- jump to the review widget ---------------- */

    /* Both the review count in the proof line and the read-more button land on
       the real review app further down the page. If no selector is configured,
       or it matches nothing, fall back to the end of the section rather than
       doing nothing — a dead control is worse than an approximate one. */
    function jumpToReviews() {
      var sel = (cfg.reviewsAnchor || '').trim();
      var node = null;
      if (sel) {
        try { node = document.querySelector(sel); }
        catch (e) { /* an invalid selector is a typo in a setting, not a crash */ }
      }
      /* Without a configured target, go to the very bottom: the review app is
         rendered after this section, so the end of the page is where it is. */
      var top = node
        ? node.getBoundingClientRect().top + (self.pageYOffset || 0) - 24
        : document.documentElement.scrollHeight;
      try {
        scrollTo({ top: top, behavior: reduceMotion() ? 'auto' : 'smooth' });
      } catch (e) {
        scrollTo(0, top);
      }
    }

    $$('[data-dqlp-scroll-reviews]').forEach(function (button) {
      button.addEventListener('click', jumpToReviews);
    });

    /* ---------------- tabs ---------------- */

    var tabButtons = $$('[data-dqlp-tab]');
    var tabPanels = $$('[data-dqlp-panel]');

    function selectTab(index, moveFocus) {
      tabButtons.forEach(function (button, i) {
        var on = i === index;
        button.setAttribute('aria-selected', on ? 'true' : 'false');
        button.tabIndex = on ? 0 : -1;
        if (on && moveFocus) button.focus();
      });
      tabPanels.forEach(function (panel, i) { panel.hidden = i !== index; });
    }

    tabButtons.forEach(function (button, index) {
      button.addEventListener('click', function () {
        selectTab(index, false);
        var wrap = $('[data-dqlp-tabs]');
        if (!wrap) return;
        var y = wrap.getBoundingClientRect().top + (self.pageYOffset || 0) - 96;
        try {
          scrollTo({ top: y, behavior: reduceMotion() ? 'auto' : 'smooth' });
        } catch (e) {
          scrollTo(0, y);
        }
      });
      button.addEventListener('keydown', function (event) {
        var last = tabButtons.length - 1;
        var next = null;
        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        selectTab(next, true);
      });
    });

    /* ---------------- countdown ---------------- */

    var countdown = $('[data-dqlp-countdown]');
    if (countdown && cfg.countdownEnabled) {
      /* Midnight tonight where the visitor actually is. setHours(24,...) rolls
         into tomorrow and handles month, year and daylight-saving boundaries,
         which is why it beats adding 86400000 to anything. */
      var endOfVisitorDay = function () {
        var d = new Date();
        d.setHours(24, 0, 0, 0);
        return d.getTime();
      };
      var endOfDayMode = cfg.countdownMode !== 'fixed';
      var deadline = endOfDayMode ? endOfVisitorDay() : Date.parse(cfg.deadline);
      var hoursNode = $('[data-dqlp-hrs]');
      var minutesNode = $('[data-dqlp-min]');
      var secondsNode = $('[data-dqlp-sec]');

      var pad = function (value) {
        return (value < 10 ? '0' : '') + value;
      };

      var tick = function () {
        /* An unset or already-past deadline must never render dashes.
           A broken timer is the first thing she sees at the top of the page
           and it tells her the site does not work. Roll to the next midnight
           instead, which is true every day and needs no maintenance. */
        if (isNaN(deadline)) {
          deadline = endOfVisitorDay();
          endOfDayMode = true;
        }
        var remaining = Math.floor((deadline - Date.now()) / 1000);
        if (remaining <= 0) {
          /* the day turned over, or a fixed deadline elapsed — roll forward */
          deadline = endOfVisitorDay();
          endOfDayMode = true;
          remaining = Math.floor((deadline - Date.now()) / 1000);
        }
        countdown.hidden = false;
        var hours = Math.min(99, Math.floor(remaining / 3600));
        if (hoursNode) hoursNode.textContent = pad(hours);
        if (minutesNode) minutesNode.textContent = pad(Math.floor((remaining % 3600) / 60));
        if (secondsNode) secondsNode.textContent = pad(remaining % 60);
        /* the drawer runs off this same second, never its own */
        paintDrawerUrgency(remaining);
      };

      var stopCountdown = function () {
        if (state.countdownTimer) {
          clearInterval(state.countdownTimer);
          state.countdownTimer = 0;
        }
      };

      if (endOfDayMode) countdown.hidden = false;
      tick();
      if (!countdown.hidden) {
        state.countdownTimer = setInterval(tick, 1000);
      }
    }

    /* ---------------- UTM capture ---------------- */

    function captureUtm() {
      var found = {};
      var any = false;
      try {
        new URLSearchParams(location.search).forEach(function (value, key) {
          if (/^utm_/i.test(key) || key === 'gclid' || key === 'fbclid' || key === 'ttclid') {
            found[key.toLowerCase()] = value;
            any = true;
          }
        });
      } catch (e) { /* older browser, no attribution */ }

      if (any) {
        try { sessionStorage.setItem('dqlp_utm', JSON.stringify(found)); } catch (e) {}
        return found;
      }
      try {
        return JSON.parse(sessionStorage.getItem('dqlp_utm') || '{}') || {};
      } catch (e) {
        return {};
      }
    }

    state.utm = captureUtm();

    /* ---------------- cart ---------------- */

    function fetchCart() {
      return fetch(cfg.routes.cart + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(function (res) { return res.json(); });
    }

    function isManagedLine(item) {
      if (cfg.managedVariantIds.indexOf(item.id) !== -1) return true;
      var props = item.properties || {};
      return !!props._gift;
    }

    function isGiftLine(item) {
      var props = item.properties || {};
      return !!props._gift;
    }

    /* Tier change replaces our lines instead of stacking them. */
    function removeManagedLines() {
      var current = state.cart ? Promise.resolve(state.cart) : fetchCart();
      return current.then(function (cart) {
        var updates = {};
        var found = false;
        ((cart && cart.items) || []).forEach(function (item) {
          if (isManagedLine(item)) {
            updates[item.key] = 0;
            found = true;
          }
        });
        if (!found) return cart;
        return postJSON(cfg.routes.cartUpdate, { updates: updates });
      }).catch(function () {
        return null;
      });
    }

    function applyAttributes(tier) {
      var attributes = {};
      var dirty = false;

      if (!state.utmApplied) {
        Object.keys(state.utm).forEach(function (key) {
          attributes[key] = state.utm[key];
          dirty = true;
        });
        state.utmApplied = true;
      }

      /* A gift that is not a cart line reaches the warehouse as nothing at all,
         and whoever packs the box has no way to know it was promised. A cart
         attribute survives checkout and shows on the order in the admin, so the
         page and the packing slip say the same thing. */
      var titles = unlockedGifts(tier).map(function (giftConfig) { return giftConfig.title; });
      if (titles.length) {
        attributes['Free gifts'] = titles.join(', ');
        dirty = true;
      }

      if (!dirty) return fetchCart();
      return postJSON(cfg.routes.cartUpdate, { attributes: attributes })
        .catch(function () { return fetchCart(); });
    }

    function setBusy(busy) {
      state.busy = busy;
      $$('[data-dqlp-add]').forEach(function (button) {
        button.disabled = busy;
        var label = button.querySelector('[data-dqlp-text]');
        if (!label) return;
        if (busy) {
          if (!label.getAttribute('data-dqlp-idle')) {
            label.setAttribute('data-dqlp-idle', label.textContent);
          }
          label.textContent = cfg.strings.ctaBusy || 'Adding…';
        } else {
          var idle = label.getAttribute('data-dqlp-idle');
          if (idle != null) {
            label.textContent = idle;
            label.removeAttribute('data-dqlp-idle');
          }
        }
      });
    }

    function addToCart() {
      if (state.busy) return;
      var tier = tierById(state.tierId);
      var parts = tier && tier.componentItems && tier.componentItems.length
        ? tier.componentItems
        : (tier && tier.variantId ? [{ id: tier.variantId, quantity: 1 }] : []);

      /* Three different failures used to print the one "sold out" sentence, so
         a screenshot of it said nothing about which had happened. They are
         separated here: no product connected is a setup fault and belongs on a
         different screen entirely from a stock problem. */
      if (!tier) {
        showError(cfg.strings.genericError + ' (no tier selected)');
        return;
      }
      if (!parts.length) {
        showError(cfg.strings.genericError + ' (this tier has no product connected)');
        return;
      }
      if (!tier.available) {
        showError(cfg.strings.soldOut);
        return;
      }

      clearError();
      setBusy(true);

      /* A tier is either one product or a list of them. Both end up as one
         request, so the drawer still opens once. */
      var items = parts.map(function (part) {
        return { id: part.id, quantity: part.quantity || 1 };
      });
      var giftLines = (tier.giftVariantIds || []).filter(Boolean);
      giftLines.forEach(function (id) {
        items.push({ id: id, quantity: 1, properties: { _gift: 'true' } });
      });

      removeManagedLines()
        .then(function () {
          /* One request for the whole kit, so the drawer opens once and the
             cart never passes through a half-built state. */
          return postJSON(cfg.routes.cartAdd, { items: items });
        })
        .then(function () {
          return applyAttributes(tier);
        })
        .then(function (cart) {
          state.cart = cart;
          renderDrawer();
          openDrawer();
          syncHeaderCount(cart);
          track('add_to_cart', {
            variant_id: tier.variantId,
            tier_name: tier.name,
            tier_id: tier.id,
            value: Number(cart && cart.total_price ? cart.total_price : tier.priceCents) / 100,
            currency: currencyCode(),
            items: cartItemsPayload(cart)
          });
        })
        .catch(function (error) {
          var status = error ? error.status : 0;
          var body = (error && error.body) || {};
          /* Shopify's own description names the product and the reason it
             refused. It beats anything written here, so it wins whenever it
             exists; the fallbacks carry the status so the three remaining
             cases stay distinguishable from a screenshot alone. */
          var detail = body.description || body.message;
          if (detail) {
            showError(detail);
          } else if (status === 422) {
            showError(cfg.strings.soldOut);
          } else {
            showError(cfg.strings.genericError + (status ? ' (HTTP ' + status + ')' : ' (no response)'));
          }

          /* Only blame the tier when nothing else was in the request. A gift
             line that cannot be added returns the same 422, and disabling a
             perfectly buyable kit over it is how a working page starts
             refusing sales for a reason nobody can see. */
          if (status === 422 && !giftLines.length) markUnavailable(tier);

          if (window.console && window.console.error) {
            window.console.error('[dqlp] add to cart failed', { status: status, body: body, items: items });
          }
        })
        .then(function () {
          setBusy(false);
        });
    }

    function changeLine(key, quantity) {
      if (state.busy) return;
      setDrawerBusy(true);
      postJSON(cfg.routes.cartChange, { id: key, quantity: quantity })
        .then(function (cart) {
          state.cart = cart;
          renderDrawer();
          syncHeaderCount(cart);
        })
        .catch(function () { /* the drawer keeps the last good cart */ })
        .then(function () { setDrawerBusy(false); });
    }

    function syncHeaderCount(cart) {
      if (!cfg.syncHeaderCount || !cart) return;
      try {
        var count = cart.item_count;
        toArray(document.querySelectorAll('.cart-count-bubble')).forEach(function (bubble) {
          var visible = bubble.querySelector('span[aria-hidden="true"]');
          if (visible) visible.textContent = count;
        });
      } catch (e) { /* the header is the theme's, never a hard dependency */ }
    }

    /* ---------------- drawer ---------------- */

    var drawer = $('[data-dqlp-drawer]');
    var drawerPanel = $('[data-dqlp-drawer-panel]');
    var drawerLines = $('[data-dqlp-drawer-lines]');

    /* The Meta in-app browser, named. Ad traffic overwhelmingly arrives in
       Facebook's and Instagram's embedded webview — the Clarity error log's
       _AutofillCallbackHandler entries are its fingerprint — and it is the
       one environment where the express wallets are least trustworthy:
       Apple Pay is generally unavailable outside Safari, and the detection
       that is supposed to hide its button runs on our custom page template,
       where it cannot be assumed to run at all. A wallet button that renders
       and then does nothing, for exactly the population the merchant cannot
       reproduce on their own phone, is how 21 carts become 1 checkout while
       every test in Safari passes.

       In the in-app browsers the drawer therefore keeps one path to payment:
       the form POST, which cannot be broken by a webview. Real Safari and
       Chrome keep their wallets. */
    var inAppBrowser = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|MicroMessenger/i
      .test((navigator && navigator.userAgent) || '');
    if (inAppBrowser && drawer) drawer.setAttribute('data-dqlp-inapp', 'true');
    /* Authored on the drawer element rather than in the schema, so delivery
       copy can be corrected in one place without a deploy. Empty attribute,
       no row — the shipping line simply goes back to what it said before. */
    var shippingHint = (drawer && drawer.getAttribute('data-dqlp-shipping-hint')) || '';
    /* The mystery gift's hint, authored on the drawer the same way. Read with
       hasAttribute rather than by truthiness, so an empty value is a decision
       — "no hint" — and not a fall-through to whatever the section setting
       happens to still contain. That distinction is the whole point of having
       the override: blanking it in the template has to actually blank it. */
    var mysteryHintSet = !!(drawer && drawer.hasAttribute('data-dqlp-mystery-hint'));
    var mysteryHint = mysteryHintSet ? drawer.getAttribute('data-dqlp-mystery-hint') : null;

    function setDrawerBusy(busy) {
      if (drawerLines) drawerLines.setAttribute('aria-busy', busy ? 'true' : 'false');
      $$('[data-dqlp-stepper-btn]').forEach(function (button) { button.disabled = busy; });
    }

    /* Which tier the cart is actually holding.

       Matching on the first variant alone broke the moment two tiers shared a
       component: both of these start with the device, so the device-only tier
       always won the race, the kit's gifts stayed locked, and the drawer said
       "2 of 3" over a cart that had earned all three. Match the whole component
       set instead, and let the most specific match win — a cart holding the
       device AND both oils is the kit, not the device on its own. */
    function tierInCart(cart) {
      var held = {};
      ((cart && cart.items) || []).forEach(function (item) {
        var id = item.variant_id || item.id;
        held[id] = (held[id] || 0) + (item.quantity || 0);
      });

      var best = null;
      var bestParts = 0;
      cfg.tiers.forEach(function (tier) {
        var parts = (tier.componentItems && tier.componentItems.length)
          ? tier.componentItems
          : (tier.variantId ? [{ id: tier.variantId, quantity: 1 }] : []);
        if (!parts.length) return;
        var complete = parts.every(function (part) {
          return (held[part.id] || 0) >= (part.quantity || 1);
        });
        if (complete && parts.length > bestParts) {
          best = tier;
          bestParts = parts.length;
        }
      });
      return best;
    }

    /* What one unit of a line is worth before the offer — the figure the page
       anchors against, not the quantity-multiplied one. Used for ordering, so
       that buying four bottles of oil can never push the oil above the device:
       the question being answered is "which of these is the main thing", and
       that does not change with quantity. */
    function unitValueCents(item) {
      var id = item.variant_id || item.id;
      var unit = (cfg.valueAt || {})[id];
      if (unit == null) unit = (cfg.compareAt || {})[id];
      if (unit == null) {
        var qty = item.quantity || 1;
        unit = Math.round((item.original_line_price || item.final_line_price || 0) / qty);
      }
      return unit || 0;
    }

    /* The hero first, whatever order the cart happens to hold.

       Shopify returns cart lines in the order they were added, and the kit adds
       the oil before the device — so the drawer opened on a $26 accessory and
       the $59 device read as the add-on underneath it. That is the wrong
       anchor: the device is the thing the ad sold and the thing the spa-price
       comparison rests on, so it has to be the first number she sees.

       Sorted by unit worth, descending, and stable: equal-worth lines keep the
       order the cart gave them, gifts stay where the gift logic puts them, and
       nothing here changes what is in the cart or what it costs. It is purely
       the reading order. */
    function orderedItems(items) {
      return items
        .map(function (item, index) { return { item: item, index: index }; })
        .sort(function (a, b) {
          var aGift = isGiftLine(a.item) ? 1 : 0;
          var bGift = isGiftLine(b.item) ? 1 : 0;
          if (aGift !== bGift) return aGift - bGift;
          var diff = unitValueCents(b.item) - unitValueCents(a.item);
          if (diff !== 0) return diff;
          return a.index - b.index;
        })
        .map(function (entry) { return entry.item; });
    }

    /* A gift row, on one line.

       Every gift used to be drawn as a full product row — 48px of artwork, a
       title, a FREE GIFT tag, then a separate footer holding the struck value
       and the word Free. Three of those ran about 240pt, which is more of the
       list than the two things being paid for got, in a drawer where the goods
       were already losing to the footer. They also announced themselves three
       ways at once: the tag said free, the price said Free, and the header
       above said all three were unlocked.

       So: title and price share the row, the tag goes (the green Free on the
       right is the same statement, made once), and the artwork drops to a mark
       beside the text rather than a tile. The hint keeps its own line, because
       the two that carry one — when shipping arrives, and that the mystery
       gift lands as a credit — are the rows saying something the title cannot,
       and the second is a disclosure rather than a flourish. */
    function buildGiftRow(opts) {
      var cls = 'dqlp-line dqlp-line--gift dqlp-line--compact';
      if (opts.quiet) cls += ' dqlp-line--quiet';
      var line = el('div', cls);

      var media = el('div', 'dqlp-line__media');
      if (opts.image) {
        var img = el('img', 'dqlp-img');
        img.src = opts.image;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
      }
      line.appendChild(media);

      var body = el('div', 'dqlp-line__body');

      var head = el('div', 'dqlp-line__head');
      head.appendChild(el('div', 'dqlp-line__title', opts.title || ''));

      var prices = el('div', 'dqlp-line__prices');
      if (opts.was) prices.appendChild(el('span', 'dqlp-line__was', opts.was));
      prices.appendChild(
        el('span', 'dqlp-line__price dqlp-line__price--free', cfg.strings.drawerFree || 'Free')
      );
      head.appendChild(prices);
      body.appendChild(head);

      line.appendChild(body);
      return line;
    }

    function buildLine(item, isHero) {
      var gift = isGiftLine(item);

      /* A gift that happens to sit in the cart as a real $0 line is still a
         gift, and has to look like the promised ones beside it — otherwise the
         same offer renders two different shapes depending on whether Shopify
         could carry it as a line, which is an implementation detail no
         customer should be able to see. */
      if (gift) {
        var giftWas = (cfg.compareAt || {})[item.variant_id || item.id] || 0;
        var giftLineWas = Math.max(giftWas * (item.quantity || 1), item.original_line_price || 0);
        return buildGiftRow({
          image: item.image ? sizedImage(item.image, 96) : '',
          title: item.product_title || item.title,
          was: giftLineWas > 0 ? money(giftLineWas) : '',
          hint: ''
        });
      }

      var line = el('div', 'dqlp-line' + (isHero ? ' dqlp-line--hero' : ''));

      var media = el('div', 'dqlp-line__media');
      if (item.image) {
        var img = el('img', 'dqlp-img');
        img.src = sizedImage(item.image, 160);
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        media.appendChild(img);
      }
      line.appendChild(media);

      var body = el('div', 'dqlp-line__body');
      body.appendChild(el('div', 'dqlp-line__title', item.product_title || item.title));

      if (item.variant_title && item.variant_title !== 'Default Title') {
        body.appendChild(el('div', 'dqlp-line__variant', item.variant_title));
      }

      var foot = el('div', 'dqlp-line__foot');

      var stepper = el('div', 'dqlp-stepper');

      var minus = el('button', 'dqlp-stepper__btn', '−');
      minus.type = 'button';
      minus.setAttribute('data-dqlp-stepper-btn', '');
      minus.setAttribute('aria-label', cfg.strings.decreaseLabel || 'Decrease quantity');
      minus.addEventListener('click', function () {
        changeLine(item.key, Math.max(0, item.quantity - 1));
      });

      var value = el('span', 'dqlp-stepper__val', String(item.quantity));

      var plus = el('button', 'dqlp-stepper__btn', '+');
      plus.type = 'button';
      plus.setAttribute('data-dqlp-stepper-btn', '');
      plus.setAttribute('aria-label', cfg.strings.increaseLabel || 'Increase quantity');
      plus.addEventListener('click', function () {
        changeLine(item.key, item.quantity + 1);
      });

      stepper.appendChild(minus);
      stepper.appendChild(value);
      stepper.appendChild(plus);
      foot.appendChild(stepper);

      var priceWrap = el('div');
      /* What this line was worth before, whichever figure is higher: a real
         cart discount, or the compare-at the page is advertising. The cart API
         only knows the first, which is why a $59 device that is half price sat
         in the drawer with no struck price at all. */
      var unitWas = (cfg.compareAt || {})[item.variant_id || item.id] || 0;
      var lineWas = Math.max(unitWas * (item.quantity || 1), item.original_line_price || 0);
      if (lineWas > item.final_line_price) {
        priceWrap.appendChild(el('span', 'dqlp-line__was', money(lineWas)));
      }
      if (item.final_line_price === 0) {
        priceWrap.appendChild(
          el('span', 'dqlp-line__price dqlp-line__price--free', cfg.strings.drawerFree || 'Free')
        );
      } else {
        priceWrap.appendChild(el('span', 'dqlp-line__price', money(item.final_line_price)));
      }
      foot.appendChild(priceWrap);

      body.appendChild(foot);
      line.appendChild(body);
      return line;
    }

    /* A gift the order includes but the cart does not carry as a line.
       The mystery gift is always one of these — putting a coupon in the cart
       would tell her what it is and kill the mechanic — and any gift whose
       product cannot be a $0 line becomes one too.

       These rows are honest rather than decorative: every gift here is $0, so
       the total the customer sees and pays is identical either way, and the
       whole set is written onto the order as a cart attribute, so what was
       promised on the page is what the packing slip says. */
    function buildPromisedLine(giftConfig) {
      /* The hint line, which only the mystery gift used to get. Shipping needs
         it more: "tracked & insured" is a quality, not an answer, and the
         question underneath it is when. Delivery is this offer's softest
         number, and a soft number handled here is an objection closed, while
         the same number discovered at checkout is the surprise that produces
         the largest single share of abandoned carts. Authored per gift, with
         the drawer's own attribute as the fallback for shipping.

         It is also the only reason a gift row is ever two lines instead of
         one: what the mystery gift actually is, and when the parcel lands.
         Neither is decoration, and the first is a disclosure. */
      var hint = giftConfig.hint || '';
      if (!hint && giftConfig.isMystery) {
        hint = mysteryHintSet ? mysteryHint : (cfg.strings.mysteryHint || '');
      }
      if (!hint && giftConfig.role === 'shipping') hint = shippingHint;

      /* Named by its own gift card, the mystery one included. It used to take
         a separate `mystery_line_label` setting, which is how a gift called
         "Mystery Gift" everywhere on the page turned into "Mystery gift —
         revealed after checkout" in the drawer: two fields for one name, and
         nothing keeping them in step. */
      return buildGiftRow({
        image: giftConfig.image,
        title: giftConfig.title || cfg.strings.mysteryLine || '',
        hint: hint,
        was: giftConfig.valueCents > 0 ? giftConfig.value : '',
        /* Shipping still recedes: it is the one gift that arrives as a service
           rather than as a thing, so it closes the list with a gap above it. */
        quiet: giftConfig.role === 'shipping'
      });
    }

    /* Every gift the chosen tier unlocks, whether or not it is a real line.

       Shipping is pushed to the end regardless of where the merchant put its
       block. It is the only one that is not a thing in the box, so it belongs
       after the things — and the gift that earns the kit should never be sitting
       underneath a delivery promise. Everything else keeps its authored order. */
    function unlockedGifts(tier) {
      var open = (cfg.gifts || []).filter(function (giftConfig) {
        if (!giftConfig.inStack) return false;
        if (giftConfig.requiresKit && !(tier && tier.unlocksAllGifts)) return false;
        return true;
      });
      var goods = [];
      var services = [];
      open.forEach(function (giftConfig) {
        (giftConfig.role === 'shipping' ? services : goods).push(giftConfig);
      });
      return goods.concat(services);
    }

    /* What one cart line is worth before the offer, per the page.

       Deliberately not `compareAt`, which is what the LINE shows struck. The
       two answer different questions and the page has always treated them so:
       the oil row prints its own sale price struck, while the value stack's
       total leaves that sale out, because stacking the oil's discount into the
       kit's anchor inflates a saving nobody can check. `valueAt` carries the
       stack's figures, so the drawer's total can only ever agree with the
       number printed above the buy button.

       Floored at what is actually being charged, so an item this page knows
       nothing about — something added from another page — can never make the
       saving look bigger than it is. */
    function lineValueCents(item) {
      var id = item.variant_id || item.id;
      var qty = item.quantity || 1;
      var unit = (cfg.valueAt || {})[id];
      if (unit == null) unit = (cfg.compareAt || {})[id];
      var worth = unit == null ? 0 : unit * qty;
      return Math.max(worth, item.original_line_price || 0, item.final_line_price || 0);
    }

    /* The sale timer, repeated where the decision is made.

       Not a second clock: this is the same `remaining` the announcement bar is
       counting down, handed over on each tick, so the drawer cannot say
       anything the top of the page is not already saying. With the countdown
       switched off there is no honest number to show and the strip stays
       hidden. */
    function paintDrawerUrgency(remaining) {
      var nodes = $$('[data-dqlp-drawer-urgency]');
      if (!nodes.length) return;
      var template = cfg.strings.drawerUrgency;
      if (!template || !remaining || remaining <= 0) {
        nodes.forEach(function (node) { node.hidden = true; });
        return;
      }
      var two = function (value) { return (value < 10 ? '0' : '') + value; };
      var timer =
        two(Math.min(99, Math.floor(remaining / 3600))) + ':' +
        two(Math.floor((remaining % 3600) / 60)) + ':' +
        two(remaining % 60);
      nodes.forEach(function (node) {
        var slot = node.querySelector('[data-dqlp-urgency-text]') || node;
        fillInto(slot, template, { timer: timer }, 'dqlp-drawer__urgency-time');
        node.hidden = false;
      });
    }

    function renderDrawer() {
      if (!drawerLines) return;
      var cart = state.cart;
      drawerLines.textContent = '';

      var items = (cart && cart.items) || [];

      if (!items.length) {
        drawerLines.appendChild(el('div', 'dqlp-drawer__empty', cfg.strings.drawerEmpty || ''));
      } else {
        /* Hero first, and only the first paid line gets the treatment: two
           heroes is no hero, and the point of the weighting is that one row
           answers "what am I actually buying" before the eye reaches the
           rest. */
        var heroSeen = false;
        orderedItems(items).forEach(function (item) {
          var isHero = !heroSeen && !isGiftLine(item);
          if (isHero) heroSeen = true;
          drawerLines.appendChild(buildLine(item, isHero));
        });
      }

      var cartTier = tierInCart(cart) || tierById(state.tierId);
      var promised = [];

      if (items.length) {
        /* Which gifts are already sitting in the cart as their own line. Those
           are drawn by buildLine above; drawing them again here would show the
           same gift twice. */
        var lineVariants = {};
        items.forEach(function (item) { lineVariants[item.variant_id || item.id] = true; });

        unlockedGifts(cartTier).forEach(function (giftConfig) {
          if (giftConfig.cartVariantId && lineVariants[giftConfig.cartVariantId]) return;
          promised.push(giftConfig);
          drawerLines.appendChild(buildPromisedLine(giftConfig));
        });
      }

      /* Totals are re-rendered from the /cart.js response every time. */
      var subtotal = cart ? cart.items_subtotal_price : 0;
      var discount = cart ? cart.total_discount : 0;
      var total = cart ? cart.total_price : 0;

      /* What the page spent a screen establishing, arriving in the drawer.

         It used to end on "Subtotal 75,38 / Total 75,38" — two identical
         numbers and no trace of the 200,33 the value stack had just totted up,
         so the last thing she saw before checkout was the price with the offer
         taken off it. This is the same arithmetic the stack does, run over the
         cart instead of over the tier, so it survives a quantity change: every
         line at what the page says it is worth, plus the gifts that are
         promised rather than posted, minus what she is actually paying. */
      var valueCents = 0;
      items.forEach(function (item) { valueCents += lineValueCents(item); });
      promised.forEach(function (giftConfig) { valueCents += giftConfig.valueCents || 0; });
      var savings = valueCents - total;
      var showSaving = items.length > 0 && savings > 0;
      /* Floor, never round: a saving is the one number you do not talk up. */
      var savingPct = valueCents > 0 ? Math.floor((savings * 100) / valueCents) : 0;

      var valueRow = $('[data-dqlp-value-row]');
      if (valueRow) valueRow.hidden = !showSaving;
      var valueNode = $('[data-dqlp-totalvalue]');
      if (valueNode) valueNode.textContent = money(valueCents);

      var saveRow = $('[data-dqlp-save-row]');
      if (saveRow) saveRow.hidden = !showSaving;
      var saveLabel = $('[data-dqlp-save-label]');
      if (saveLabel) {
        saveLabel.textContent = fill(cfg.strings.drawerSaveLabel, { pct: String(savingPct) });
      }
      var saveValue = $('[data-dqlp-save]');
      if (saveValue) saveValue.textContent = money(savings);

      var subtotalNode = $('[data-dqlp-subtotal]');
      if (subtotalNode) subtotalNode.textContent = money(subtotal);

      var discountRow = $('[data-dqlp-discount-row]');
      var discountNode = $('[data-dqlp-discount]');
      if (discountRow) discountRow.hidden = !(discount > 0);
      if (discountNode) discountNode.textContent = '−' + money(discount);

      var totalNode = $('[data-dqlp-grandtotal]');
      if (totalNode) totalNode.textContent = money(total);

      /* How many things, and at what quantity, without scrolling for it.
         Counted in units rather than lines, because "2 items" over a cart
         holding two bottles and a device is the kind of true-but-wrong number
         that gets noticed at checkout. Gifts are counted separately: they are
         not what she is paying for, and folding them in inflates the count. */
      var countNode = $('[data-dqlp-drawer-count]');
      if (countNode) {
        var paidUnits = 0;
        items.forEach(function (item) {
          if (!isGiftLine(item)) paidUnits += item.quantity || 0;
        });
        var giftCount = promised.length;
        items.forEach(function (item) { if (isGiftLine(item)) giftCount += 1; });
        if (!paidUnits && !giftCount) {
          countNode.hidden = true;
          countNode.textContent = '';
        } else {
          var parts = [];
          if (paidUnits) parts.push(paidUnits + (paidUnits === 1 ? ' item' : ' items'));
          if (giftCount) parts.push(giftCount + (giftCount === 1 ? ' gift' : ' gifts'));
          countNode.textContent = ' · ' + parts.join(' + ');
          countNode.hidden = false;
        }
      }

      var checkoutLabel = $('[data-dqlp-checkout-label]');
      if (checkoutLabel) {
        var checkoutTemplate = cfg.strings.drawerCheckout;
        /* The total belongs on the button. Without it the last figure she read
           is two rows and one guarantee ago, and "wait, how much was it" is a
           question answered by scrolling back rather than by tapping. If the
           merchant's label already has the token, it is placed where they put
           it; if it does not, it is appended rather than ignored, so the
           behaviour does not depend on a setting nobody remembers to fill in. */
        if (String(checkoutTemplate || '').indexOf('[total]') === -1 && items.length) {
          checkoutTemplate = String(checkoutTemplate || '') + ' · [total]';
        }
        checkoutLabel.textContent = fill(checkoutTemplate, { total: money(total) });
      }
      var checkoutButton = $('[data-dqlp-checkout]');
      if (checkoutButton) checkoutButton.disabled = !items.length;

      renderGiftProgress(cartTier, items.length > 0);
      renderUpgrade();
      markScrollable();
    }

    /* Whether the list actually has anything below the fold. The fade at its
       bottom edge is there to say "there is more"; on a cart short enough to
       fit, the same fade half-erases the final row for no reason. Measured
       after layout, and again on resize, because the answer changes with the
       keyboard, with rotation, and with a quantity going from 1 to 2. */
    function markScrollable() {
      if (!drawerLines) return;
      var check = function () {
        var over = drawerLines.scrollHeight - drawerLines.clientHeight > 2;
        drawerLines.setAttribute('data-scrollable', over ? 'true' : 'false');
      };
      check();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(check);
    }

    function renderGiftProgress(tier, hasItems) {
      var wrap = $('[data-dqlp-progress]');
      if (!wrap) return;
      /* Only the cards that are actually gifts. The guarantee is in this list
         too, but it is not something that arrives in the box — counting it made
         the drawer read "4 of 4 gifts unlocked" underneath an announcement bar
         that said "3 free gifts". Same set the value stack and the bar use. */
      var gifts = (cfg.gifts || []).filter(function (giftConfig) { return giftConfig.inStack; });
      if (!gifts.length || !hasItems) {
        wrap.hidden = true;
        return;
      }
      var open = unlockedGifts(tier);
      var unlocked = open.length;
      var percent = Math.round((unlocked / gifts.length) * 100);
      var complete = unlocked >= gifts.length;

      /* A progress indicator at 100%, with nothing left to do about it, is a
         label. "All 3 gifts unlocked" was the third place on this screen
         saying the same thing — the header counts the gifts, and each gift
         sits in the list below with its value struck and Free beside it — and
         it was the only one of the three that used the word "unlocked" over a
         set that unlocks automatically, which promises an achievement and then
         delivers a statement.

         So the band earns its row only while something is still locked, where
         "2 of 3" is real information about a choice she can still make. When
         the set is complete, the gifts prove themselves. */
      if (complete) {
        wrap.hidden = true;
        return;
      }

      wrap.hidden = false;
      wrap.setAttribute('data-complete', 'false');
      var text = $('[data-dqlp-progress-text]');
      if (text) {
        /* Only ever the partial template now — the complete one returned
           above, because there is nothing left to make progress towards. */
        text.textContent = fill(cfg.strings.drawerGiftProgress, {
          unlocked: String(unlocked),
          total: String(gifts.length)
        });
      }

      /* One segment per gift rather than a percentage of a single bar. Two of
         three gifts on a continuous track is a stripe of colour that means
         nothing at a glance; three boxes with two lit reads as "two of three"
         without the label underneath having to say it. */
      var bar = $('[data-dqlp-progress-bar]');
      if (bar) {
        bar.setAttribute('aria-valuenow', String(percent));
        bar.hidden = false;
        bar.textContent = '';
        var isOpen = {};
        open.forEach(function (giftConfig) { isOpen[giftConfig.id] = true; });
        gifts.forEach(function (giftConfig) {
          var seg = el('div', 'dqlp-drawer__progress-seg');
          seg.setAttribute('data-on', isOpen[giftConfig.id] ? 'true' : 'false');
          seg.title = giftConfig.title || '';
          bar.appendChild(seg);
        });
      }
    }

    /* ---------------- drawer open / close, focus trap ---------------- */

    function focusables() {
      if (!drawerPanel) return [];
      return toArray(
        drawerPanel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (node) {
        return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
      });
    }

    function onKeydown(event) {
      if (!state.drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      var nodes = focusables();
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function lockScroll(lock) {
      var body = document.body;
      if (lock) {
        var gap = self.innerWidth - document.documentElement.clientWidth;
        body.setAttribute('data-dqlp-locked-overflow', body.style.overflow || '');
        body.setAttribute('data-dqlp-locked-padding', body.style.paddingRight || '');
        body.style.overflow = 'hidden';
        if (gap > 0) body.style.paddingRight = gap + 'px';
      } else {
        body.style.overflow = body.getAttribute('data-dqlp-locked-overflow') || '';
        body.style.paddingRight = body.getAttribute('data-dqlp-locked-padding') || '';
        body.removeAttribute('data-dqlp-locked-overflow');
        body.removeAttribute('data-dqlp-locked-padding');
      }
    }

    function openDrawer() {
      if (!drawer || state.drawerOpen) return;
      state.drawerOpen = true;
      state.lastFocus = document.activeElement;
      drawer.setAttribute('data-open', 'true');
      lockScroll(true);
      document.addEventListener('keydown', onKeydown, true);
      var first = drawer.querySelector('[data-dqlp-drawer-first]');
      if (first) first.focus();
      else if (drawerPanel) drawerPanel.focus();
    }

    function closeDrawer() {
      if (!drawer || !state.drawerOpen) return;
      state.drawerOpen = false;
      drawer.setAttribute('data-open', 'false');
      lockScroll(false);
      document.removeEventListener('keydown', onKeydown, true);
      if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
        state.lastFocus.focus();
      }
      state.lastFocus = null;
    }

    $$('[data-dqlp-drawer-close]').forEach(function (button) {
      button.addEventListener('click', closeDrawer);
    });

    /* ---------------- the device→Kit upgrade ---------------- */

    /* One-tap tier upgrade, offered in the drawer, only in the one cart state
       it is true for: exactly one device, no oil, kit tier buyable.

       The published record on one-tap offers made after the decision: a 4.7%
       average take rate across 40,000+ merchants, top decile near 28%, and
       the converging rules are one offer, reason before feature, one tap to
       accept. All three are honoured here. The guideline cap of 25-40% of
       order value is exceeded — the delta runs about half the device price —
       but that guideline is written for add-on cross-sells; this is a tier
       swap whose reason the page has already argued twice, and the reference
       for the pattern runs a delta of 139% on the same mechanic.

       Accepting re-uses addToCart wholesale: select the kit tier and run the
       same path the buy box runs, which already clears the managed lines and
       adds the kit in one request — so the cart never passes through a state
       where she owns half of each tier, and a failure leaves the device order
       intact rather than empty. */
    var upgradeDismissed = false;
    var upgradeSeen = false;
    var UPG_KEY = 'dqlp-upgrade-dismissed';

    function upgradeWasDismissed() {
      if (upgradeDismissed) return true;
      try { return sessionStorage.getItem(UPG_KEY) === '1'; } catch (e) { return false; }
    }
    function dismissUpgrade() {
      upgradeDismissed = true;
      try { sessionStorage.setItem(UPG_KEY, '1'); } catch (e) { /* memory flag holds */ }
    }

    function upgradeOffer() {
      var tiers = cfg.tiers || [];
      var kit = null;
      var solo = null;
      tiers.forEach(function (tier) {
        if (tier.includesOil && tier.available && !kit) kit = tier;
        if (!tier.includesOil && !solo) solo = tier;
      });
      if (!kit || !solo) return null;

      /* The offer only tells the truth about one cart: a single device and
         nothing else paid. Two devices, an extra oil she added herself, any
         other paid line — the "+[delta]" arithmetic stops describing what
         accepting would do, and an upgrade whose price is wrong once is a
         drawer that is lying. */
      var items = (state.cart && state.cart.items) || [];
      var soloIds = {};
      (solo.componentItems || []).forEach(function (part) { soloIds[part.id] = true; });
      var paid = items.filter(function (item) { return !isGiftLine(item); });
      if (paid.length !== 1) return null;
      var line = paid[0];
      if (!soloIds[line.variant_id || line.id] || line.quantity !== 1) return null;

      var delta = (kit.priceCents || 0) - (solo.priceCents || 0);
      if (delta <= 0) return null;

      return { kit: kit, deltaCents: delta };
    }

    function renderUpgrade() {
      var box = $('[data-dqlp-upgrade]');
      if (!box) return;
      var title = drawer ? drawer.getAttribute('data-dqlp-upgrade-title') : '';
      var offer = title && !upgradeWasDismissed() ? upgradeOffer() : null;

      box.textContent = '';
      if (!offer) {
        box.hidden = true;
        return;
      }

      var body = drawer.getAttribute('data-dqlp-upgrade-body') || '';
      var acceptTemplate = drawer.getAttribute('data-dqlp-upgrade-accept') || 'Upgrade · +[delta]';
      var declineLabel = drawer.getAttribute('data-dqlp-upgrade-decline') || '';

      box.appendChild(el('div', 'dqlp-drawer__upgrade-title', title));
      if (body) box.appendChild(el('div', 'dqlp-drawer__upgrade-body', body));

      var accept = el(
        'button',
        'dqlp-drawer__upgrade-accept',
        fill(acceptTemplate, { delta: money(offer.deltaCents) })
      );
      accept.type = 'button';
      accept.addEventListener('click', function () {
        if (state.busy) return;
        track('upsell_accept', {
          tier_id: offer.kit.id,
          tier_name: offer.kit.name,
          value: offer.deltaCents / 100,
          currency: currencyCode()
        });
        /* The buy box's own path, verbatim: select the kit, add the kit. It
           clears the device line and the gift lines itself and re-renders the
           drawer when the cart comes back. */
        selectTier(offer.kit.id);
        addToCart();
      });
      box.appendChild(accept);

      if (declineLabel) {
        var decline = el('button', 'dqlp-drawer__upgrade-decline', declineLabel);
        decline.type = 'button';
        decline.addEventListener('click', function () {
          track('upsell_decline', { tier_id: offer.kit.id });
          dismissUpgrade();
          renderUpgrade();
        });
        box.appendChild(decline);
      }

      box.hidden = false;

      /* Counted once per session, not once per render, or the view number is
         a measure of how often the drawer repaints instead of how many people
         saw the offer — and the take rate divides by it. */
      if (!upgradeSeen) {
        upgradeSeen = true;
        track('upsell_view', {
          tier_id: offer.kit.id,
          value: offer.deltaCents / 100,
          currency: currencyCode()
        });
      }
    }

    /* ---------------- the three panels, as collapsed sections ---------------- *
     *
     * Third shape for this content, and the reasoning in full, because the
     * first two were both wrong in different ways. Horizontal tabs hide two
     * thirds of the content and Baymard's testing shows core content gets
     * overlooked in that layout. Fully expanded sections — the last version —
     * fixed discoverability by tripling the visible page, and the merchant's
     * verdict on the result was correct: nobody signs up for that much
     * scroll. Baymard endorses a second replacement that solves both at
     * once: COLLAPSED sections — every heading visible, every body one tap
     * away, no content silently unreachable and no wall of text. That is
     * what this is.
     *
     * FOLD_OPEN lists which panels start open (0-based). Empty keeps them
     * all folded — maximum compression. [2] would open Reviews by default,
     * which is worth testing later: the brand file says the buying trigger
     * is the third positive testimonial.
     */
    var TABS_COLLAPSED = true;
    var FOLD_OPEN = [];

    function expandTabs() {
      if (!TABS_COLLAPSED) return;
      var wrap = $('[data-dqlp-tabs]');
      if (!wrap || !tabPanels.length) return;

      tabPanels.forEach(function (panel, index) {
        var id = panel.getAttribute('data-dqlp-panel');
        var button = wrap.querySelector('[data-dqlp-tab="' + id + '"]');
        var label = button ? (button.textContent || '').trim() : '';
        var startOpen = FOLD_OPEN.indexOf(index) !== -1;

        var head = el('button', 'dqlp-fold__head');
        head.type = 'button';
        head.setAttribute('aria-expanded', startOpen ? 'true' : 'false');
        head.appendChild(el('span', 'dqlp-fold__label', label));
        var chev = el('span', 'dqlp-fold__chev');
        chev.setAttribute('aria-hidden', 'true');
        head.appendChild(chev);

        panel.hidden = !startOpen;
        panel.removeAttribute('tabindex');
        panel.setAttribute('role', 'region');
        panel.removeAttribute('aria-labelledby');
        if (label) panel.setAttribute('aria-label', label);

        head.addEventListener('click', function () {
          var open = head.getAttribute('aria-expanded') === 'true';
          head.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
          if (!open) track('fold_open', { label: label });
        });

        panel.parentNode.insertBefore(head, panel);
      });

      var list = wrap.querySelector('.dqlp-tabs__list');
      if (list) {
        list.setAttribute('hidden', '');
        list.setAttribute('aria-hidden', 'true');
      }
      wrap.setAttribute('data-folded', 'true');
    }

    /* ---------------- the theme's own bar, actually gone ---------------- */

    /* The stylesheet the section emits targets the announcement bar by the
       name Shopify's own Dawn gives it — a section id ending
       `__announcement-bar`. Any theme that calls it anything else, and every
       app that injects its own strip, sails straight through: which is how a
       landing page ended up carrying a second sale banner above its own, in
       a different currency, linking to a different product page.

       Naming is the wrong thing to match on. What is actually true is that
       exactly one section in the header group is the header; everything else
       up there is decoration this page did not ask for. So find the header by
       what it contains and hide its neighbours, whatever they are called. */
    function trimHeaderGroup() {
      if (!cfg.themeChrome || cfg.themeChrome === 'keep') return;
      var hideAll = cfg.themeChrome === 'hide_all';

      toArray(document.querySelectorAll('.shopify-section-group-header-group'))
        .forEach(function (node) {
          if (root.contains(node)) return;
          var isHeader = !!node.querySelector(
            'header, .header, sticky-header, [class*="site-header"], [class*="header__"]'
          );
          if (hideAll || !isHeader) node.style.setProperty('display', 'none', 'important');
        });

      /* Themes that keep the bar outside the group, and apps that inject one
         after the fact. Matched on class rather than id, and never on
         anything inside our own root. */
      toArray(document.querySelectorAll(
        '.announcement-bar-section, .shopify-section.announcement-bar, ' +
        '.utility-bar, .announcement-bar__announcement'
      )).forEach(function (node) {
        if (root.contains(node)) return;
        var host = closest(node, '.shopify-section') || node;
        if (root.contains(host)) return;
        host.style.setProperty('display', 'none', 'important');
      });
    }

    /* ---------------- the theme's cart belongs to this page ---------------- */

    /* Closing this drawer and tapping the header cart used to open the theme's
       own drawer — the same cart, told a completely different story. Nothing
       in it knows about the gifts, the value stack, the timer or the saving,
       so a customer one tap from checkout was handed a bare list of line items
       and the offer she had just been reading evaporated.

       Suppressing the theme drawer AFTER it opens was never enough: it fixed
       the symptom on a page where the theme drawer is a custom element and did
       nothing at all where the cart icon is a plain link to /cart, which is a
       full navigation away from the landing page and the surest exit on the
       screen. So the click is taken at the source instead. */
    function isCartTrigger(node) {
      if (!node || !node.closest) return false;
      /* Our own drawer, buy box and sticky bar wire themselves up. */
      if (closest(node, '[data-dqlp-root]')) return false;

      var link = closest(node, 'a[href]');
      if (link) {
        var href = (link.getAttribute('href') || '').split('#')[0].split('?')[0];
        if (/(^|\/)cart\/?$/.test(href)) return true;
      }
      return !!closest(
        node,
        '#cart-icon-bubble, .header__icon--cart, [data-cart-drawer-toggle], ' +
        '.js-drawer-open-cart, [href$="/cart"], cart-drawer summary, ' +
        '[aria-controls="cart-drawer"], [data-action="open-cart"]'
      );
    }

    function onCartTriggerClick(event) {
      if (!cfg.suppressThemeDrawer || !drawer) return;
      if (event.defaultPrevented) return;
      /* Middle-click and the modifier keys mean "open this somewhere else",
         which is a request to leave and not ours to override. */
      if (event.button != null && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!isCartTrigger(event.target)) return;

      event.preventDefault();
      event.stopPropagation();

      /* Opened first, refreshed second. The cart is almost always already in
         state, so waiting on the network before showing anything would put a
         blank frame between her tap and the panel — and if the request fails
         the drawer still opens on the last good cart rather than on nothing. */
      openDrawer();
      fetchCart().then(renderDrawer).catch(function () {});
    }

    document.addEventListener('click', onCartTriggerClick, true);

    /* ---------------- the theme's own drawer stays shut here ---------------- */

    function suppressThemeDrawer() {
      if (!cfg.suppressThemeDrawer) return;
      ['cart-drawer', 'cart-notification'].forEach(function (selector) {
        var node = document.querySelector(selector);
        if (!node) return;
        var shut = function () {
          if (!node.classList.contains('active') && !node.classList.contains('animate')) return;
          if (typeof node.close === 'function') {
            try { node.close(); } catch (e) { /* fall through */ }
          }
          node.classList.remove('active');
          node.classList.remove('animate');
        };
        shut();
        try {
          var watcher = new MutationObserver(shut);
          watcher.observe(node, { attributes: true, attributeFilter: ['class'] });
          observers.push(watcher);
        } catch (e) { /* nothing to observe */ }
      });
    }

    /* ---------------- wiring ---------------- */

    $$('[data-dqlp-tier-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) selectTier(input.value, true);
      });
    });

    $$('[data-dqlp-tier]').forEach(function (label) {
      label.addEventListener('click', function (event) {
        if (label.getAttribute('data-available') === 'false') event.preventDefault();
      });
    });

    $$('[data-dqlp-add]').forEach(function (button) {
      button.addEventListener('click', addToCart);
    });

    var checkoutForm = $('[data-dqlp-checkout-form]');
    if (checkoutForm) {
      checkoutForm.addEventListener('submit', function () {
        var cart = state.cart;
        track('begin_checkout', {
          value: Number(cart && cart.total_price ? cart.total_price : 0) / 100,
          currency: currencyCode(),
          items: cartItemsPayload(cart)
        });
      });
    }

    /* ---------------- first paint ---------------- */

    var startTier = tierById(state.tierId);
    if (!startTier || !startTier.available) startTier = firstAvailableTier();
    if (startTier) applyTier(startTier);

    verifyStylesheet();
    hoistBar();
    publishBarHeight();
    applyStickyOffset();
    fitHeadlines();
    suppressThemeDrawer();
    trimHeaderGroup();
    expandTabs();
    /* Again after load: a bar injected by an app arrives after this script
       runs, and a strip that appears three seconds in is worse than one that
       was there all along. */
    self.addEventListener('load', trimHeaderGroup);

    var offsetTimer = 0;
    function onResize() {
      clearTimeout(offsetTimer);
      offsetTimer = setTimeout(function () {
        publishBarHeight();
        applyStickyOffset();
        fitHeadlines();
      }, 150);
    }
    self.addEventListener('resize', onResize, { passive: true });

    try {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(fitHeadlines).catch(function () {});
      }
    } catch (e) {}

    /* Prime the cart so a tier swap does not need an extra round trip. */
    var prime = function () {
      fetchCart().then(function (cart) {
        state.cart = cart;
        renderDrawer();
      }).catch(function () { /* the page works without it */ });
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(prime, { timeout: 2000 });
    else setTimeout(prime, 600);

    /* ---------------- theme editor ---------------- */

    /* Clicking a block in the editor's sidebar scrolls the preview to it — but
       only if it can be seen. Three quarters of this page's blocks cannot:
       reviews, FAQs and timeline stages sit inside tab panels that are display
       none, FAQ answers are inside a collapsed <details>, and gallery slides,
       specialists and before/afters are parked off-screen in horizontal rails.
       Selecting one of those looked like the editor had ignored the click.
       This opens whatever the block is buried in, first. */
    function revealBlock(node) {
      var panel = closest(node, '[data-dqlp-panel]');
      if (panel) {
        var panelIndex = tabPanels.indexOf(panel);
        if (panelIndex > -1) selectTab(panelIndex, false);
      }

      var details = closest(node, 'details');
      if (details) details.open = true;

      var card = closest(node, '.dqlp-rail__item');
      var rail = closest(card || node, '.dqlp-rail');
      if (rail && card) {
        var slideAttr = card.getAttribute('data-dqlp-slide');
        if (slideAttr !== null) {
          goToSlide(parseInt(slideAttr, 10) || 0);
        } else {
          var left = card.offsetLeft - rail.offsetLeft - 20;
          try { rail.scrollTo({ left: left, behavior: 'smooth' }); }
          catch (e) { rail.scrollLeft = left; }
        }
      }

      /* Centre it vertically rather than aligning to the top: the announcement
         bar is pinned up there and would cover whatever we scrolled to. */
      try { (card || node).scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      catch (e) { /* the editor's own scroll still happened */ }
    }

    function destroy() {
      dropBar();
      clearInterval(state.countdownTimer);
      clearTimeout(offsetTimer);
      self.removeEventListener('resize', onResize);
      self.removeEventListener('load', trimHeaderGroup);
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('click', onCartTriggerClick, true);
      if (state.drawerOpen) lockScroll(false);
      observers.forEach(function (watcher) {
        try { watcher.disconnect(); } catch (e) {}
      });
      observers.length = 0;
    }

    instances.push({ root: root, reveal: revealBlock, destroy: destroy });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function boot() {
    toArray(document.querySelectorAll('[data-dqlp-root]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ------------------------------------------------------------------ *
   * theme editor lifecycle
   * ------------------------------------------------------------------ */

  document.addEventListener('shopify:section:load', function (event) {
    dropDetached();
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    toArray(target.querySelectorAll('[data-dqlp-root]')).forEach(init);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    for (var i = instances.length - 1; i >= 0; i--) {
      if (target && target.contains && target.contains(instances[i].root)) dropInstance(i);
    }
    dropDetached();
  });

  document.addEventListener('shopify:block:select', function (event) {
    var entry = instanceFor(event.target);
    if (entry) entry.reveal(event.target);
  });
})();